/**
 * 手机号短信验证控件（附件1「手机号真实性验证」方式一，2026-10-16 补实现）
 * -----------------------------------------------------------------------------
 * 表单里 PHONE 类型字段的配套控件：填号 → 过图形码 → 发码 → 填码 → 验证。
 * 验证状态落在**服务端**（24h 免验标记，按 userId + 号码哈希隔离），
 * 这里的「已验证」只是即时反馈；提交时由 `PHONE-005` 强门槛兜底。
 *
 * 改号后本地验证态立即作废（服务端标记同样按号码隔离，换号必须重验）。
 *
 * 图形验证码（2026-10-27 加）：服务端原有的 60s 冷却 / 当日 10 条全部按号码隔离，
 * 换号即换配额，脚本遍历手机号就能把短信费用刷穿。所以「获取验证码」拆成两步：
 * 点按钮 = 打开图形码浮层，填对图才真正下发短信。
 *
 * 为什么是**浮层**而不是就地展开一行（2026-10-27 真机反馈后改）：就地展开时，
 * 点完按钮只是旁边多出一行长得和短信码一模一样的输入框，既不说明「开始了新一步」，
 * 也不说明「发出去了没有」。浮层有标题和「取消 / 确认发送」两个明确出口 ——
 * 关上就是没发，不会留下半截状态。实现见 `components/CaptchaModal`。
 *
 * 倒计时（2026-10-27 改）：不再用组件内的自减计数器，改用 `lib/send-cooldown`
 * —— 那层锁**与号码无关**，改号 / 关弹窗重开 / 刷新页面都不解锁。原因见该模块注释：
 * 只按号码锁的话，服务端留的「改号可立刻重发」这个善意口子，会被前端当成连点通道。
 */
import { Input, Toast } from 'antd-mobile';
import { useState } from 'react';
import { describeApiError } from '../../api/errors';
import { sendPhoneCode, verifyPhoneCode } from '../../api/h5';
import { useProfileBasic } from '../../api/hooks';
import { CaptchaModal, type CaptchaSubmit } from '../../components/CaptchaModal';
import { maskPhone } from '../../lib/phone-format';
import { isPhoneVerified } from '../../lib/phone-verified';
import { useSendCooldown } from '../../lib/send-cooldown';

const MOBILE_PATTERN = /^1[3-9]\d{9}$/;

interface PhoneVerifyFieldProps {
  value: unknown;
  disabled: boolean;
  onChange: (next: string) => void;
  onBlur: () => void;
  /**
   * 验证通过时上报**通过的那个号码**（父级据此显示「已验证」）。
   * 传号码而不是「通过了」这个事实：改号后服务端标记同样按号码隔离，
   * 只存布尔值会让「验过 139…，改成 138…」也被判成已验。
   */
  onVerified?: (verifiedPhone: string) => void;
  /**
   * 是否自渲状态胶囊。放在「身份核验」卡里时由卡片头部统一表达状态，
   * 这里再挂一枚「短信已验证」就成了同一句话写两遍。
   */
  showStatus?: boolean;
}

export function PhoneVerifyField({
  value,
  disabled,
  onChange,
  onBlur,
  onVerified,
  showStatus = true,
}: PhoneVerifyFieldProps) {
  const phone = typeof value === 'string' ? value : '';
  const [code, setCode] = useState('');
  // 「已验证」派生自「验证通过时锁定的号码」：换号即失效，不需要 effect 重置
  const [verifiedFor, setVerifiedFor] = useState<string | null>(null);
  // 发码倒计时与号码无关：改号、重挂载都不解锁（见 lib/send-cooldown）
  const { remaining: cooldown, markSent } = useSendCooldown();
  /** 是否已发过码：决定要不要露出验证码输入行 */
  const [codeSent, setCodeSent] = useState(false);
  /** 验证码是服务端回显自动填进去的（仅本地/测试），用于给一行提示 */
  const [autoFilled, setAutoFilled] = useState(false);
  /** 图形码浮层是否打开（点「获取验证码」才开，不占常驻视觉空间） */
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const phoneOk = MOBILE_PATTERN.test(phone.trim());
  /** 账号资料：用于「同号已验过」的免验判断（只在报名表单里有意义） */
  const profile = useProfileBasic();

  /**
   * 「已验证」两条来源合并：本页刚验过的那个号，或账号级免验。
   * 判定抽在 `lib/phone-verified.ts` —— 身份核验页的门禁用的是同一份，
   * 两处各写一遍曾导致「控件显示已验证、门禁判成未验」的死锁。
   */
  const verified = isPhoneVerified({
    verifiedFor,
    phone,
    profile: profile.data ?? null,
  });

  /**
   * 提交验证码（不含 busy 前置检查 —— 发码后自动校验时 busy 已置位）。
   * 入参显式传入而不是读 `code` state：`setCode` 之后同一个闭包里读到的还是旧值。
   */
  const submitCode = async (value: string) => {
    await verifyPhoneCode(phone.trim(), value.trim());
    setVerifiedFor(phone.trim());
    onVerified?.(phone.trim());
  };

  /**
   * 用浮层交出来的人机校验凭据发码。
   * **失败直接抛给浮层**（它负责换图 + 提示原因），这里只管成功之后的收尾 ——
   * 组件里再 `catch` 一次就会变成两句提示、两次换图。
   */
  const send = async ({ captchaId, captchaCode }: CaptchaSubmit) => {
    const result = await sendPhoneCode(phone.trim(), { captchaId, captchaCode });
    setCodeSent(true);
    // 用服务端回传的时长落锁（口径只有一处），取不到时模块内部退回 60s
    markSent(result.cooldownSeconds);
    if (result.debugCode) {
      /**
       * 调试回显（仅本地/测试）：一期短信通道是 mock，码不会到达手机。
       * 直接填入并校验，省掉「翻 Redis 取码再手敲」这一整套动作。
       * 生产不会走到这里（服务端恒不返回 `debugCode`）。
       */
      setCode(result.debugCode);
      setAutoFilled(true);
      await submitCode(result.debugCode);
      // 走通了才报「验证通过」——失败交给调用方，别先夸一句再报错
      Toast.show({ icon: 'success', content: '验证通过（测试环境已自动填入）' });
    } else {
      // 说出「发到哪个号了」—— 这一步是用户判断「到底发没发」的唯一依据
      Toast.show({ icon: 'success', content: `验证码已发送至 ${maskPhone(phone.trim())}` });
    }
  };

  const verify = async () => {
    if (disabled || !phoneOk || code.trim().length !== 6 || busy) return;
    setBusy(true);
    try {
      await submitCode(code);
      Toast.show({ icon: 'success', content: '验证成功' });
    } catch (error) {
      Toast.show({ icon: 'fail', content: describeApiError(error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="phone-verify">
      <div className="phone-verify-row phone-verify-row--between">
        <div className="field-box phone-verify__input">
          <Input
            type="tel"
            inputMode="numeric"
            maxLength={11}
            value={phone}
            placeholder="请输入手机号"
            disabled={disabled}
            onChange={onChange}
            // 不传事件：原生 onBlur 的 FocusEvent 会被渲染器当成"新值"（2026-10-20 缺陷）
            onBlur={() => onBlur()}
          />
        </div>
        {verified ? (
          showStatus ? (
            <span className="kyc-badge kyc-badge--ok">短信已验证</span>
          ) : null
        ) : (
          <button
            type="button"
            className="link-text"
            disabled={disabled || !phoneOk || cooldown > 0 || busy}
            onClick={() => setCaptchaVisible(true)}
          >
            {cooldown > 0 ? `${cooldown}s 后重发` : '获取验证码'}
          </button>
        )}
      </div>

      {/* 验证码行只在发过码之后出现：没发码就占着一行输入框，视觉上很像「填错了」 */}
      {!verified && codeSent ? (
        <div className="phone-verify-row">
          <div className="field-box phone-verify__code">
            <Input
              value={code}
              inputMode="numeric"
              maxLength={6}
              placeholder="6 位验证码"
              disabled={disabled}
              onChange={setCode}
            />
          </div>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={disabled || code.trim().length !== 6 || busy}
            onClick={verify}
          >
            验证
          </button>
        </div>
      ) : null}

      {/* 调试提示：让测试者知道「这不是真实流程走通了」，而是服务端把码回显了。
          没有这一行，本地一路顺利会让人误以为短信通道已经接通。
          验证成功后也要留着 —— 恰恰是成功那一刻最容易被误读。 */}
      {autoFilled ? (
        <p className="phone-verify-hint">
          测试环境：图形码与短信码均由服务端回显并自动填入（关闭 SMS_DEBUG_CODE 可恢复手动输入）
        </p>
      ) : null}

      <CaptchaModal
        visible={captchaVisible}
        onClose={() => setCaptchaVisible(false)}
        onSubmit={send}
      />
    </div>
  );
}
